import { cs } from '../../lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  XCircle,
  CheckCircle,
  Clock,
  User,
  Eye,
  Ban,
  RefreshCw,
  FileText,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const WithdrawalRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await api.get(`/tournaments/withdrawal-requests${params}`);
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to fetch withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      await api.put(`/tournaments/withdrawal-requests/${selectedRequest.id}/approve`);
      toast.success('Withdrawal approved. Confirmation email sent to parent.');
      setApproveDialog(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      await api.put(`/tournaments/withdrawal-requests/${selectedRequest.id}/reject`, { reason: rejectReason });
      toast.success('Withdrawal request rejected');
      setRejectDialog(false);
      setRejectReason('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: <Badge className="bg-amber-100 text-amber-700">Pending</Badge>,
      approved: <Badge className="bg-green-100 text-green-700">Approved</Badge>,
      rejected: <Badge className="bg-red-100 text-red-700">Rejected</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  const getPaymentBadge = (status, amount) => {
    // Only show "Paid" when the registration is *actually* paid. The
    // registration's `payment_amount` is the tournament fee snapshot
    // (what's owed) — NOT proof of payment. Previously any registered
    // player showed "Paid $XXX" because `amount > 0` is always true.
    if (status === 'paid') {
      return <Badge className="bg-blue-100 text-blue-700" data-testid="payment-badge-paid">Paid {cs()}{amount || 0}</Badge>;
    }
    if (amount > 0) {
      return <Badge className="bg-slate-100 text-slate-600" data-testid="payment-badge-unpaid">Unpaid · {cs()}{amount} due</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600" data-testid="payment-badge-unpaid">Unpaid</Badge>;
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '-';

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <DashboardLayout title="Withdrawal Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Withdrawal Requests">
      <div className="space-y-6" data-testid="withdrawal-requests-page">
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Tournament Withdrawal Requests</h1>
            <p className="text-slate-500">Review and manage parent withdrawal requests</p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchRequests}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
          </div>
        </div>

        {/* Summary */}
        {pendingCount > 0 && (
          <Card className="border-2 border-red-300 bg-red-50/50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <XCircle className="h-6 w-6 text-red-600" />
                <span className="text-red-800 font-semibold">{pendingCount} withdrawal request{pendingCount !== 1 ? 's' : ''} pending your review</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Requests List */}
        <Card>
          <CardHeader>
            <CardTitle>Requests ({requests.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-700">No withdrawal requests</p>
                <p className="text-slate-500">
                  {statusFilter === 'pending' ? 'No pending requests to review.' : 'No requests match this filter.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map(req => (
                  <div
                    key={req.id}
                    className={`border rounded-lg p-4 ${
                      req.status === 'pending' ? 'bg-red-50/60 border-red-200' :
                      req.status === 'approved' ? 'bg-green-50/40 border-green-200' :
                      'bg-slate-50 border-slate-200'
                    }`}
                    data-testid={`withdrawal-request-${req.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {getStatusBadge(req.status)}
                          <span className="font-semibold text-slate-900">{req.player_name}</span>
                          <span className="text-slate-400">|</span>
                          <span className="text-sm text-slate-600">{req.tournament_name}</span>
                          {req.age_category && (
                            <Badge variant="outline">{req.age_category}</Badge>
                          )}
                          {getPaymentBadge(req.payment_status, req.payment_amount)}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          <strong>Reason:</strong> {req.reason}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><User className="h-3 w-3" />{req.parent_name} ({req.parent_email})</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(req.created_at)}</span>
                        </div>
                        {req.status === 'approved' && req.approved_by_name && (
                          <p className="text-xs text-green-600 mt-1">Approved by {req.approved_by_name} on {formatDate(req.approved_at)}</p>
                        )}
                        {req.status === 'rejected' && req.rejected_by_name && (
                          <p className="text-xs text-red-600 mt-1">
                            Rejected by {req.rejected_by_name} on {formatDate(req.rejected_at)}
                            {req.reject_reason && ` — ${req.reject_reason}`}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedRequest(req); setDetailDialog(true); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {req.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => { setSelectedRequest(req); setApproveDialog(true); }}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-${req.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setSelectedRequest(req); setRejectReason(''); setRejectDialog(true); }}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`reject-${req.id}`}
                            >
                              <Ban className="h-4 w-4 mr-1" />Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-red-500" /> Withdrawal Request Details
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Player</p>
                    <p className="font-medium">{selectedRequest.player_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Tournament</p>
                    <p className="font-medium">{selectedRequest.tournament_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Category</p>
                    <p className="font-medium">{selectedRequest.age_category || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Parent</p>
                    <p className="font-medium">{selectedRequest.parent_name}</p>
                    <p className="text-xs text-slate-400">{selectedRequest.parent_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Payment</p>
                    {getPaymentBadge(selectedRequest.payment_status, selectedRequest.payment_amount)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Reason for Withdrawal</p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{selectedRequest.reason}</p>
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  Submitted: {formatDate(selectedRequest.created_at)}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailDialog(false)}>Close</Button>
              {selectedRequest?.status === 'pending' && (
                <>
                  <Button
                    onClick={() => { setDetailDialog(false); setApproveDialog(true); }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setDetailDialog(false); setRejectReason(''); setRejectDialog(true); }}
                    className="text-red-600"
                  >
                    <Ban className="h-4 w-4 mr-2" />Reject
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approve Confirmation Dialog */}
        <AlertDialog open={approveDialog} onOpenChange={setApproveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Approve Withdrawal</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  Approving will cancel <strong>{selectedRequest?.player_name}'s</strong> registration 
                  from <strong>{selectedRequest?.tournament_name}</strong> and send a confirmation email to the parent.
                </span>
                {selectedRequest?.payment_amount > 0 && (
                  <span className="block bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
                    <strong>Note:</strong> This player has paid {cs()}{selectedRequest.payment_amount}. Please handle refund separately if applicable.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleApprove}
                className="bg-green-600 hover:bg-green-700"
                disabled={processing}
                data-testid="confirm-approve-btn"
              >
                {processing ? 'Processing...' : 'Approve & Notify Parent'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reject Dialog */}
        <AlertDialog open={rejectDialog} onOpenChange={setRejectDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reject Withdrawal Request</AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <span className="block">
                  Are you sure you want to reject the withdrawal request for <strong>{selectedRequest?.player_name}</strong>?
                </span>
                <span className="block">
                  <label className="text-sm font-medium text-slate-700 block mb-1">Reason (optional)</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejecting..."
                    className="w-full rounded-md border border-slate-300 p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-red-500 text-slate-900"
                    data-testid="reject-reason-input"
                  />
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReject}
                className="bg-red-600 hover:bg-red-700"
                disabled={processing}
                data-testid="confirm-reject-btn"
              >
                {processing ? 'Processing...' : 'Reject Request'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default WithdrawalRequests;
